import DashboardSkeleton from "./DashboardSkeleton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { WEB_API_DASHBOARD } from "../../env";
import { StaticImage } from "../../utils/image";
import ApiLaw from "../../api/law";
import { WatDatePicker } from "thaidatepicker-react";
import moment from "moment";
import _ from "lodash";
import Select from "react-select";
import ApiMaster from "../../api/master";
import LawDetailTable from "./LawDetailTable";

const FilterSelect = ({ label, options, value, onChange, className = "" }) => {
    const normalizedOptions = options.map((option) => (
        typeof option === "string" ? { value: option, label: option } : option
    ));
    const optionValueMap = useMemo(
        () => new Map(normalizedOptions.map((option) => [String(option.value ?? ""), option.value])),
        [normalizedOptions]
    );

    return (
        <label className={`flex flex-col gap-1 text-xs text-slate-500 ${className}`}>
            {label}
            <select
                value={value ?? ""}
                onChange={(event) => onChange(optionValueMap.get(event.target.value))}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none"
            >
                {normalizedOptions.map((option) => (
                    <option key={String(option.value ?? "")} value={String(option.value ?? "")}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
};

const DateFilterInput = ({ label, value, onChange }) => (
    <label className="flex flex-col gap-1 text-xs text-slate-500">
        {label}
        <WatDatePicker
            value={value}
            onChange={(christDate) => onChange(christDate ? moment(christDate) : null)}
            inputStyle={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "8px 12px",
                fontSize: 14,
                width: "100%",
            }}
            placeholder="วว/ดด/ปป"
            dateFormat={"yyyy-MM-dd"}
            displayFormat={"DD/MM/YYYY"}
            clearable={false}
            disabled={false}
            readOnly={false}
            yearBoundary={99}
        />
    </label>
);

const formatCellValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString("en-US");
};


const SummaryDashboard = () => {
    const history = useHistory();

    const getDefaultFiscalRange = () => {
        const now = moment();
        const isFiscalYearStart = now.month() >= 9;
        const startYear = isFiscalYearStart ? now.year() : now.year() - 1;
        const endYear = isFiscalYearStart ? now.year() + 1 : now.year();

        return {
            start: moment({ year: startYear, month: 9, date: 1 }),
            end: moment({ year: endYear, month: 8, date: 30 }),
        };
    };

    const defaultRange = getDefaultFiscalRange();
    const [startDate, setStartDate] = useState(defaultRange.start);
    const [endDate, setEndDate] = useState(defaultRange.end);
    const [selectedLawType, setSelectedLawType] = useState(0);
    const [selectedAgency, setSelectedAgency] = useState(0);
    const [selectedMinistry, setSelectedMinistry] = useState(0);

    const [agencyOption, setAgencyOption] = useState([
        { value: 0, label: "ทุกหน่วยงาน" },
    ]);
    const [ministryOption, setMinistryOption] = useState([
        { value: 0, label: "ทุกกระทรวง" },
    ]);

    const [lawHierarchyOption, setLawHierarchyOption] = useState([
        { value: 0, label: "ทุกศักดิ์กฎหมาย" },
    ]);

    const donutSeries = [11.56, 39.18, 14.1, 9.41, 9.14, 8.0, 8.61];
    const donutLabels = [
        "รัฐธรรมนูญ",
        "พระราชบัญญัติ",
        "พระราชกฤษฎีกา",
        "พระราชกำหนด",
        "กฎกระทรวง",
        "ประกาศ",
        "อื่นๆ",
    ];
    const donutColors = ["#2563eb", "#f97316", "#10b981", "#ec4899", "#a855f7", "#facc15", "#64748b"];

    const donutOptions = useMemo(
        () => ({
            chart: {
                type: "donut",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
            },
            labels: donutLabels,
            colors: donutColors,
            dataLabels: { enabled: false },
            legend: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: "70%",
                        labels: {
                            show: true,
                            value: {
                                fontSize: "20px",
                                fontWeight: 600,
                                formatter: (value) => `${value}%`,
                            },
                            total: {
                                show: true,
                                label: "รวมทั้งหมด",
                                fontSize: "12px",
                                color: "#94a3b8",
                                formatter: () => "100%",
                            },
                        },
                    },
                },
            },
        }),
        []
    );

    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardDataTable, setDashboardDataTable] = useState(null);
    const [lawDetailFilter, setLawDetailFilter] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const payload = {
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                lawType: selectedLawType ?? "",
                agency_id: selectedAgency ?? 0,
                ministry_id: selectedMinistry ?? 0,
            };

            const response = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/law-dashboard`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDashboardData(data.agencyListeningRows);
            setDashboardDataTable(data.agencyListeningRowsMonthly);
        } catch (err) {
            console.error("Error fetching agency dashboard data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [endDate, selectedLawType, startDate, selectedAgency, selectedMinistry]);



    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        getLawHierarchyOption();
        getFilterOption();
    }, []);

    const getFilterOption = async () => {
        const filter = await ApiMaster.homeFilter();
        if (filter.status == 200) {
            const { data } = filter.data;
            setAgencyOption([...agencyOption, ...data.agency]);
            setMinistryOption([...ministryOption, ...data.ministry]);
        }
    };

    const getLawHierarchyOption = async () => {
        const result = await ApiLaw.getMaster();
        if (result.status == 200) {
            const { data } = result;
            const hierarchy = Array.isArray(data?.hirachy) ? data.hirachy : [];
            setLawHierarchyOption([
                { value: 0, label: "ทุกศักดิ์กฎหมาย" },
                ...hierarchy,
            ]);
        }
    };

    const dataRows = useMemo(() => {
        if (!dashboardData) {
            return [];
        }
        return Array.isArray(dashboardData) ? dashboardData : [dashboardData];
    }, [dashboardData]);

    const activeThaiYear = useMemo(() => {
        const baseYear = endDate ? moment(endDate).year() : new Date().getFullYear();
        return String(baseYear + 543);
    }, [endDate]);

    const yearColumns = useMemo(() => {
        const keys = new Set();
        dataRows.forEach((row) => {
            Object.keys(row || {}).forEach((key) => {
                if (key.startsWith("total_") && key !== "total_all") {
                    keys.add(key);
                }
            });
        });
        const list = Array.from(keys);
        if (!list.length) {
            return [`total_${activeThaiYear}`];
        }
        return list.sort((a, b) => Number(a.replace("total_", "")) - Number(b.replace("total_", "")));
    }, [activeThaiYear, dataRows]);

    const tableRows = useMemo(() => {
        return dataRows.map((row, index) => ({
            key: `${row.law_hierarchy_name || "unknown"}-${index}`,
            lawType: row.law_hierarchy_name || "ไม่ระบุ",
            orderId: row.order_id !== null && row.order_id !== undefined && row.order_id !== "" && Number.isFinite(Number(row.order_id))
                ? Number(row.order_id)
                : Number.MAX_SAFE_INTEGER,
            totalAll: row.total_all ?? 0,
            yearTotals: yearColumns.reduce((acc, key) => {
                acc[key] = row?.[key] ?? 0;
                return acc;
            }, {}),
        })).sort((a, b) => {
            const orderDiff = a.orderId - b.orderId;
            if (orderDiff !== 0) return orderDiff;
            return a.lawType.localeCompare(b.lawType, "th", { sensitivity: "base" });
        });
    }, [dataRows, yearColumns]);

    const totalAllCount = useMemo(() => {
        return dataRows.reduce((sum, row) => {
            const value = Number(row?.total_all);
            return sum + (Number.isNaN(value) ? 0 : value);
        }, 0);
    }, [dataRows]);

    const monthlyPivot = useMemo(() => {
        const rawRows = Array.isArray(dashboardDataTable) ? dashboardDataTable : [];
        const monthKeyMap = new Map();
        rawRows.forEach((row) => {
            const year = Number(row?.year_active);
            const month = Number(row?.month_active);
            if (Number.isNaN(year) || Number.isNaN(month)) {
                return;
            }
            const key = `${year}-${String(month).padStart(2, "0")}`;
            if (!monthKeyMap.has(key)) {
                monthKeyMap.set(key, {
                    key,
                    year,
                    month,
                    label: `${row?.month_name_th || month} ${year + 543}`,
                });
            }
        });

        const columns = Array.from(monthKeyMap.values()).sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });

        const totalsByLawType = new Map();
        rawRows.forEach((row) => {
            const lawType = row?.law_hierarchy_name || "ไม่ระบุ";
            if (!totalsByLawType.has(lawType)) {
                totalsByLawType.set(lawType, {
                    months: {},
                    orderId: Number.MAX_SAFE_INTEGER,
                });
            }
            const lawTypeData = totalsByLawType.get(lawType);
            const hasOrderId = row?.order_id !== null && row?.order_id !== undefined && row?.order_id !== "";
            const parsedOrderId = Number(row?.order_id);
            if (hasOrderId && Number.isFinite(parsedOrderId)) {
                lawTypeData.orderId = Math.min(lawTypeData.orderId, parsedOrderId);
            }
            const year = Number(row?.year_active);
            const month = Number(row?.month_active);
            if (Number.isNaN(year) || Number.isNaN(month)) {
                return;
            }
            const key = `${year}-${String(month).padStart(2, "0")}`;
            const value = Number(row?.total_agencies);
            if (Number.isNaN(value)) {
                return;
            }
            lawTypeData.months[key] = (lawTypeData.months[key] || 0) + value;
        });

        const rows = Array.from(totalsByLawType.entries()).map(([lawType, data]) => ({
            key: lawType,
            lawType,
            months: data.months,
            orderId: data.orderId,
        }));

        rows.sort((a, b) => {
            const orderDiff = a.orderId - b.orderId;
            if (orderDiff !== 0) return orderDiff;
            return a.lawType.localeCompare(b.lawType, "th", { sensitivity: "base" });
        });

        return { columns, rows };
    }, [dashboardDataTable]);

    const convertYearToText = (key) => {
        if (key == "fy_latest") {
            return moment().year() + 543
        }
        else if (key == "fy_latest_1") {
            return moment().add(-1, 'years').year() + 543
        }
        else if (key == "fy_latest_2") {
            return moment().add(-2, 'years').year() + 543
        }
        else if (key == "fy_latest_3") {
            return moment().add(-3, 'years').year() + 543
        }
        else if (key == "fy_latest_4") {
            return moment().add(-4, 'years').year() + 543
        }
        else if (key == "fy_latest_5") {
            return moment().add(-5, 'years').year() + 543
        }
    }

    const summaryYearStats = useMemo(() => {
        return yearColumns.map((key) => {
            const year = key.replace("total_", "");
            const sum = dataRows.reduce((acc, row) => {
                const value = Number(row?.[key]);
                return acc + (Number.isNaN(value) ? 0 : value);
            }, 0);

            return {
                label: `ปี ${convertYearToText(year)}`,
                value: formatCellValue(sum),
            };
        });
    }, [dataRows, yearColumns]);



    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) {
            return "ทุกช่วงเวลา";
        }
        return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    }, [startDate, endDate]);

    const selectedLawLabel = useMemo(() => {
        return lawHierarchyOption.find((option) => option.value == selectedLawType)?.label || "เลือกตามศักดิ์กฎหมาย";
    }, [lawHierarchyOption, selectedLawType]);

    const selectedMinistryLabel = useMemo(() => {
        if (!selectedMinistry) {
            return "ทุกกระทรวง";
        }
        return ministryOption.find((option) => option.value == selectedMinistry)?.label || "ทุกกระทรวง";
    }, [ministryOption, selectedMinistry]);

    const selectedAgencyLabel = useMemo(() => {
        if (!selectedAgency) {
            return "ทุกหน่วยงาน";
        }
        return agencyOption.find((option) => option.value == selectedAgency)?.label || "ทุกหน่วยงาน";
    }, [agencyOption, selectedAgency]);

    useEffect(() => {
        setLawDetailFilter(null);
    }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    const showLawDetails = (row, column = null) => {
        setLawDetailFilter({
            start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
            end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
            lawType: selectedLawType ?? 0,
            agency_id: selectedAgency ?? 0,
            ministry_id: selectedMinistry ?? 0,
            law_hierarchy_name: row.lawType,
            year: column?.year || 0,
            month: column?.month || 0,
            month_label: column?.label || "",
            range_label: rangeLabel,
        });
    };

    const csvEscape = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value);
        if (/[",\n]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
    };

    const handleDownloadExcel = () => {
        const header = ["ศักดิ์กฎหมาย", ...monthlyPivot.columns.map((column) => column.label)];
        const rows = monthlyPivot.rows.map((row) => [
            row.lawType,
            ...monthlyPivot.columns.map((column) => row.months[column.key] || 0),
        ]);
        const csvContent = [header, ...rows]
            .map((row) => row.map(csvEscape).join(","))
            .join("\n");
        const blob = new Blob(["\ufeff", csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `summary-dashboard-${moment().format(
            "YYYYMMDD"
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };


    // Loading state
    if (loading) {
        return <DashboardSkeleton variant="summary" />;
    }

    // Error state
    if (error && !dashboardData) {
        return (
            <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="text-red-500 mb-4">
                                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-sm text-slate-500">เกิดข้อผิดพลาด: {error}</p>
                            <button
                                onClick={fetchDashboardData}
                                className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm hover:bg-sky-600"
                            >
                                ลองใหม่อีกครั้ง
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
            <div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-center gap-x-2">
                            <button
                                onClick={() => history.go(-1)}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-1 text-xs font-medium text-sky-600 hover:bg-slate-50"
                            >
                                <svg viewBox="0 0 24 24" className="h-4 w-4">
                                    <path
                                        d="M15 18l-6-6 6-6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                ย้อนกลับ
                            </button>
                            <p className="text-sm text-primary font-bold">สถิติจำนวนกฎหมายทั้งหมด</p>
                            <button
                                type="button"
                                onClick={handleDownloadExcel}
                                disabled={monthlyPivot.rows.length === 0}
                                className={`mb-[1px] inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${monthlyPivot.rows.length === 0
                                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                    }`}
                            >
                                ดาวน์โหลด Excel
                            </button>
                        </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-5">
                        <DateFilterInput
                            label="วันที่เริ่มต้น"
                            value={startDate}
                            onChange={setStartDate}
                        />
                        <DateFilterInput
                            label="วันที่สิ้นสุด"
                            value={endDate}
                            onChange={setEndDate}
                        />
                        {/* <FilterSelect
                            label="แยกตามศักดิ์กฎหมาย"
                            options={lawHierarchyOption}
                            value={selectedLawType}
                            onChange={setSelectedLawType}
                        /> */}
                        <div className="flex items-end gap-2">
                            <label className={`flex flex-col gap-1 text-xs text-slate-500 w-full`}>
                                {"ศักดิ์กฏหมาย"}
                                <Select
                                    className="basic-single"
                                    classNamePrefix="select"
                                    defaultValue={lawHierarchyOption[0]}
                                    options={lawHierarchyOption}
                                    value={lawHierarchyOption.find((x) => x.value == selectedLawType)}
                                    onChange={(item) => {
                                        setSelectedLawType(item.value);
                                    }}
                                />
                            </label>

                        </div>

                        <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                            {"กระทรวง"}
                            <Select
                                // className="basic-single"
                                classNames={{
                                    control: (state) =>
                                        state.isFocused ? 'rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none' : 'rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none',
                                }}
                                classNamePrefix="select"
                                defaultValue={ministryOption[0]}
                                value={ministryOption.find((x) => x.value == selectedMinistry)}
                                options={ministryOption}
                                onChange={(item) => {
                                    setSelectedMinistry(item.value);
                                    setSelectedAgency(0);
                                }}
                            />
                        </label>
                        <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                            {"หน่วยงาน"}
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                defaultValue={agencyOption[0]}
                                options={
                                    selectedMinistry > 0
                                        ? agencyOption.filter((x) => x.ministry_id == selectedMinistry)
                                        : agencyOption
                                }
                                value={agencyOption.find((x) => x.value == selectedAgency)}
                                onChange={(item) => {
                                    setSelectedAgency(item.value);
                                }}
                            />
                        </label>
                    </div>
                </section>

                {/* Summary and result panels removed per the September 2026 dashboard requirements. */}
            </div>
        </div>
    );
};

export default SummaryDashboard;
